import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of, switchMap } from 'rxjs';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ToastService } from '../../ui';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { PhoneAuthenticationSectionComponent } from './phone-authentication-section.component';
import { GenerateTanDialogComponent, GenerateTanDialogData } from './generate-tan-dialog/generate-tan-dialog.component';
import { ConfirmMobileDialogComponent, ConfirmMobileDialogData } from './confirm-mobile-dialog/confirm-mobile-dialog.component';
import { SessionService } from '../../core/session/session.service';
import { AuthService } from '../../core/auth/auth.service';
import { LoggingService } from '../../core/services/logging.service';
import { UserAccountApiService } from '../../core/services/user-account-api.service';
import { UserAccountDetail } from '../../core/services/user-account-api.model';
import { PhoneServiceApiService } from '../../core/services/phone-service-api.service';
import { buildCallViewerLink } from '../../shared/utils/call-viewer-link.util';
import { ErrorStateComponent } from '../../shared/error-state/error-state.component';
import { BoolDisplayPipe } from '../../shared/pipes/bool-display.pipe';
import { extractErrorMessage } from '../../shared/utils/extract-error-message';
import {
  HlmBadgeComponent,
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmSpinnerComponent,
  HlmSwitchComponent,
  HLM_FORM_FIELD,
} from '../../ui';

@Component({
  selector: 'cmt-user-account-info',
  imports: [
    DatePipe,
    ReactiveFormsModule,
    ErrorStateComponent,
    BoolDisplayPipe,
    HlmBadgeComponent,
    HlmBtnDirective,
    HlmIconComponent,
    HlmInputDirective,
    HlmSpinnerComponent,
    HlmSwitchComponent,
    ...HLM_FORM_FIELD,
    PhoneAuthenticationSectionComponent,
  ],
  templateUrl: './user-account-info.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserAccountInfoComponent implements OnInit {
  readonly session = inject(SessionService);
  private readonly api = inject(UserAccountApiService);
  private readonly auth = inject(AuthService);
  private readonly loggingService = inject(LoggingService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly phoneServiceApi = inject(PhoneServiceApiService);

  readonly userAccount = signal<UserAccountDetail | null>(null);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly editMode = signal(false);
  readonly saving = signal(false);

  /** Related phone services for the current UA */
  readonly relatedPhoneServices = computed(() => {
    const ua = this.userAccount();
    if (!ua) return [];
    return this.session.phoneServices().filter(ps => ps.userAccountId === ua.id);
  });

  /** User account is considered active for either English or German status value */
  readonly userAccountActive = computed(() => {
    const status = this.userAccount()?.status;
    return status === 'active' || status === 'aktiv';
  });

  /** Go-to-Contract enabled only when at least one phone service is linked. */
  readonly canGoToContract = computed(() => this.relatedPhoneServices().length >= 1);

  /** Password reset enabled only if mobile is set and confirmed */
  readonly canResetPassword = computed(() => {
    const ua = this.userAccount();
    return !!ua?.mobile && ua.mobileConfirmed;
  });

  /**
   * The Password Reset card is collapsed by default: an occasional action, and it keeps the page short.
   *
   * @ticket EB-4453
   */
  readonly resetExpanded = signal(false);

  /** Expands or collapses the Password Reset card. */
  toggleReset(): void {
    this.resetExpanded.update(open => !open);
  }

  /**
   * "Send confirmation code" is offered only for a mobile number that is set but not yet confirmed.
   *
   * @ticket EB-4453
   */
  readonly canConfirmMobile = computed(() => {
    const ua = this.userAccount();
    return !!ua?.mobile && !ua.mobileConfirmed;
  });

  readonly resetPasswordDisabledReason = computed(() => {
    const ua = this.userAccount();
    if (!ua?.mobile) return 'No mobile number set';
    if (!ua.mobileConfirmed) return 'Mobile number not confirmed';
    return '';
  });

  /** Pre-fetched NEW portal base URL (null = not available for this brand) */
  readonly newPortalUrl = signal<string | null>(null);
  readonly newPortalChecked = signal(false);

  readonly statusBadgeTone = computed<'success' | 'destructive' | 'secondary' | 'accent' | 'default'>(() => {
    const status = this.userAccount()?.status;
    switch (status) {
      case 'active': case 'aktiv': return 'success';
      case 'deactivated': case 'deaktiv': return 'destructive';
      case 'formerCustomer': return 'secondary';
      case 'futureCustomer': return 'accent';
      default: return 'default';
    }
  });

  /** Translate API status (German/mixed) to English display label */
  readonly statusLabel = computed(() => {
    const status = this.userAccount()?.status;
    const map: Record<string, string> = {
      active: 'Active', aktiv: 'Active',
      deactivated: 'Inactive', deaktiv: 'Inactive',
      formerCustomer: 'Former', formerService: 'Former',
      futureCustomer: 'Future', futureService: 'Future',
      neverActiveService: 'Never Active',
    };
    return map[status ?? ''] ?? status ?? '';
  });

  readonly form = new FormGroup({
    salutation: new FormControl('', { nonNullable: true }),
    academicTitle: new FormControl('', { nonNullable: true }),
    firstName: new FormControl('', { nonNullable: true }),
    lastName: new FormControl('', { nonNullable: true }),
    email: new FormControl('', { nonNullable: true }),
    mobile: new FormControl('', { nonNullable: true }),
    username: new FormControl('', { nonNullable: true }),
    missedCallNotificationEnabledByCustomer: new FormControl(false, { nonNullable: true }),
    missedCallNotificationEnabledByMarketing: new FormControl(false, { nonNullable: true }),
    enabled2FactorAuthentication: new FormControl(false, { nonNullable: true }),
  });

  ngOnInit(): void {
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const id = Number(params['userAccountId']);
      if (id) {
        this.loadUserAccount(id);
      }
    });
  }

  /** Reload UA data using the last route param. Called from error-state retry. */
  reloadUserAccount(): void {
    const id = Number(this.route.snapshot.params['userAccountId']);
    if (id) this.loadUserAccount(id);
  }

  private loadUserAccount(userAccountId: number): void {
    this.loading.set(true);
    this.error.set(null);
    this.editMode.set(false);
    this.resetExpanded.set(false);

    this.api.getById(userAccountId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (ua) => {
        this.userAccount.set(ua);
        this.loading.set(false);
        this.checkNewPortalAvailability$().pipe(
          takeUntilDestroyed(this.destroyRef),
        ).subscribe();
      },
      error: (err) => {
        this.error.set(extractErrorMessage(err, 'Failed to load user account'));
        this.loading.set(false);
      },
    });
  }

  startEdit(): void {
    const ua = this.userAccount();
    if (!ua) return;
    this.form.patchValue({
      salutation: ua.salutation,
      academicTitle: ua.academicTitle,
      firstName: ua.firstName,
      lastName: ua.lastName,
      email: ua.email,
      mobile: ua.mobile,
      username: ua.username,
      missedCallNotificationEnabledByCustomer: ua.missedCallNotificationEnabledByCustomer,
      missedCallNotificationEnabledByMarketing: ua.missedCallNotificationEnabledByMarketing,
      enabled2FactorAuthentication: ua.enabled2FactorAuthentication,
    });
    this.editMode.set(true);
  }

  cancelEdit(): void {
    this.form.reset();
    this.editMode.set(false);
  }

  save(): void {
    const ua = this.userAccount();
    if (!ua || this.form.invalid) return;

    this.saving.set(true);
    const updated: UserAccountDetail = {
      ...ua,
      salutation: this.form.controls.salutation.value,
      academicTitle: this.form.controls.academicTitle.value,
      firstName: this.form.controls.firstName.value,
      lastName: this.form.controls.lastName.value,
      email: this.form.controls.email.value,
      mobile: this.form.controls.mobile.value,
      username: this.form.controls.username.value,
      missedCallNotificationEnabledByCustomer: this.form.controls.missedCallNotificationEnabledByCustomer.value,
      missedCallNotificationEnabledByMarketing: this.form.controls.missedCallNotificationEnabledByMarketing.value,
      enabled2FactorAuthentication: this.form.controls.enabled2FactorAuthentication.value,
    };

    this.api.save(updated).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (saved) => {
        // EB-3811: PUT /useraccount/v1/useraccounts can return 200 with an
        // empty / partial body (no `id`) — accepting that silently used to
        // crash on `saved.id`. Fall back to the optimistically-built `updated`
        // payload (carries the existing id + just-saved form values) when the
        // server omits the canonical shape.
        const fresh: UserAccountDetail =
          saved && saved.id != null
            ? { ...saved, id: Number(saved.id) }
            : { ...updated, id: Number(updated.id) };
        this.userAccount.set(fresh);
        this.editMode.set(false);
        this.saving.set(false);
        this.toast.open('User account saved', 'OK', { duration: 3000 });
      },
      error: () => {
        this.saving.set(false);
        this.toast.open('Failed to save user account', 'OK', { duration: 5000 });
      },
    });
  }

  resetPassword(): void {
    const ua = this.userAccount();
    if (!ua) return;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Reset Password',
        message: `Reset password for ${ua.firstName} ${ua.lastName} and send via SMS to ${ua.mobile}?`,
        confirmLabel: 'Reset',
        confirmColor: 'warn',
        icon: 'lock_reset',
      },
    });

    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(confirmed => {
      if (!confirmed) return;

      this.api.resetPassword(ua.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => this.toast.open('Password reset and sent via SMS', 'OK', { duration: 3000 }),
        error: () => this.toast.open('Failed to reset password', 'OK', { duration: 5000 }),
      });
    });
  }

  /**
   * Confirms the mobile number via SMS code (old-CMT flow). On success the account is
   * reloaded so `mobileConfirmed` flips and Reset Password becomes available.
   *
   * @ticket EB-4453
   */
  confirmMobile(): void {
    const ua = this.userAccount();
    if (!ua || !this.canConfirmMobile()) return;

    const data: ConfirmMobileDialogData = {
      userAccountId: ua.id,
      fullName: `${ua.firstName} ${ua.lastName}`.trim(),
      mobile: ua.mobile,
    };

    this.dialog.open(ConfirmMobileDialogComponent, { data }).afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.toast.open('Mobile number confirmed', 'OK', { duration: 3000 });
        // The route may have changed while the dialog was open: only touch the account that is still shown.
        const stillShown = (current: UserAccountDetail | null): current is UserAccountDetail =>
          current?.id === ua.id;
        this.api.getById(ua.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (fresh) => {
            if (stillShown(this.userAccount())) this.userAccount.set(fresh);
          },
          // The backend accepted the code, so the flag is true even if the reload failed.
          error: () => this.userAccount.update(current =>
            stillShown(current) ? { ...current, mobileConfirmed: true } : current),
        });
      });
  }

  /** Pre-fetch portal URL. On success: cache + enable/disable button. On error: leave button enabled (lazy check on click). */
  private checkNewPortalAvailability$() {
    const brandId = this.session.brandId();
    if (!brandId) return of(null);
    return this.api.getNewPortalUrl(brandId).pipe(
      switchMap(url => {
        this.newPortalUrl.set(url);
        this.newPortalChecked.set(true);
        return of(url);
      }),
      catchError(() => of(null)), // silently fail — button stays enabled, lazy check on click
    );
  }

  openNewPortal(): void {
    const ua = this.userAccount();
    if (!ua) return;

    // Use cached URL if available, otherwise fetch on demand
    const cached = this.newPortalUrl();
    const url$ = cached ? of(cached) : this.api.getNewPortalUrl(this.session.brandId() ?? 0);

    url$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (portalBaseUrl) => {
        if (!portalBaseUrl) {
          this.newPortalUrl.set(null);
          this.newPortalChecked.set(true);
          this.toast.open('New portal not available for this customer', 'OK', { duration: 5000, tone: 'error' });
          return;
        }
        this.newPortalUrl.set(portalBaseUrl);
        this.newPortalChecked.set(true);
        const token = this.auth.accessToken;
        if (!token) {
          this.toast.open('Not authenticated — please log in again', 'OK', { duration: 5000, tone: 'error' });
          return;
        }
        const params = new URLSearchParams({
          access_token: token,
          access_type: 'side_entrance',
          access_by: String(this.auth.currentUserId),
          access_for: String(ua.id),
        });
        /** @ticket EB-3798 — usage: jump-out to the New customer portal (side entrance). */
        this.loggingService.usage('side_entrance.click', { target: 'new_portal', section: 'user_account' });
        window.open(`${portalBaseUrl}?${params}`, '_blank', 'noopener');
      },
      error: () => this.toast.open('Failed to load portal URL', 'OK', { duration: 5000 }),
    });
  }

  openOldPortal(): void {
    const ua = this.userAccount();
    if (!ua) return;

    this.api.getRendezvousKey(ua.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result) => {
        if (!result?.key) {
          this.toast.open('Failed to generate portal access key', 'OK', { duration: 5000 });
          return;
        }
        const url = this.api.buildOldPortalUrl(result.key, this.auth.currentUserId);
        /** @ticket EB-3798 — usage: jump-out to the Old customer portal (side entrance). */
        this.loggingService.usage('side_entrance.click', { target: 'old_portal', section: 'user_account' });
        window.open(url, '_blank', 'noopener');
      },
      error: () => this.toast.open('Failed to get portal key', 'OK', { duration: 5000 }),
    });
  }

  navigateToPhoneService(psId: number): void {
    this.router.navigate(['ps', psId, 'settings'], { relativeTo: this.route.parent });
  }

  /**
   * Deactivate this user account. UA-only operation — no contract/PS side
   * effects (EB-3811: replaces the old end-contract-cascade flow).
   */
  deactivate(): void {
    const ua = this.userAccount();
    if (!ua) return;
    const fullName = `${ua.firstName} ${ua.lastName}`.trim();
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Deactivate user account',
        message: `Deactivate ${fullName}? This deactivates the call handling — CAI will no longer handle calls for this user. You can reactivate later.`,
        confirmLabel: 'Deactivate',
        confirmColor: 'warn',
        icon: 'block',
      },
    }).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(confirmed => {
      if (!confirmed) return;
      this.api.deactivate(ua.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.toast.open('User account deactivated', 'OK', { duration: 3000 });
          this.session.reloadCustomer();
          this.reloadUserAccount();
        },
        error: () => this.toast.open('Failed to deactivate user account', 'OK', { duration: 5000 }),
      });
    });
  }

  /** Reactivate this user account. UA-only operation (EB-3811). */
  reactivate(): void {
    const ua = this.userAccount();
    if (!ua) return;
    const fullName = `${ua.firstName} ${ua.lastName}`.trim();
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Reactivate user account',
        message: `Reactivate ${fullName}? They regain login access immediately.`,
        confirmLabel: 'Reactivate',
        confirmColor: 'primary',
        icon: 'check_circle',
      },
    }).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(confirmed => {
      if (!confirmed) return;
      this.api.activate(ua.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.toast.open('User account reactivated', 'OK', { duration: 3000 });
          this.session.reloadCustomer();
          this.reloadUserAccount();
        },
        error: () => this.toast.open('Failed to reactivate user account', 'OK', { duration: 5000 }),
      });
    });
  }

  /**
   * Jump to the related contract from this UA. Read-only navigation — the
   * contract view is where actual cancel/modify lives.
   *
   *   1 related PS  → resolve contractId via PhoneServiceApiService.getById,
   *                   navigate to contracts with queryParams.contractId for
   *                   auto-expand/scroll on arrival.
   *   >1 related PS → navigate to contracts list, no preselection.
   *   0 related PS  → button disabled (canGoToContract).
   */
  goToContract(): void {
    const related = this.relatedPhoneServices();
    if (related.length === 0) return;
    if (related.length === 1) {
      this.phoneServiceApi.getById(related[0].id).pipe(
        takeUntilDestroyed(this.destroyRef),
      ).subscribe({
        next: (psDetail) => {
          const contractId = psDetail.contractId;
          this.router.navigate(['contracts'], {
            relativeTo: this.route.parent,
            queryParams: contractId !== undefined && contractId !== null
              ? { contractId }
              : undefined,
          });
        },
        error: () => this.toast.open('Failed to resolve contract for this account', 'OK', { duration: 5000 }),
      });
      return;
    }
    this.router.navigate(['contracts'], { relativeTo: this.route.parent });
  }

  /** Open the Call Viewer with this UA's customer + UA pre-filtered (EB-3811). */
  openCallViewerForUa(): void {
    const ua = this.userAccount();
    if (!ua) return;
    const link = buildCallViewerLink('ua', {
      customerId: ua.customerId,
      userAccountId: ua.id,
    });
    this.router.navigate(link.commands, { queryParams: link.queryParams });
  }

  /** Open the Generate TAN dialog with the current UA context. */
  openGenerateTan(): void {
    const ua = this.userAccount();
    if (!ua) return;

    const customer = this.session.customer();
    const customerName = customer
      ? [customer.firstName, customer.lastName].filter(Boolean).join(' ') || customer.companyName || `Customer ${customer.id}`
      : `Customer ${ua.customerId}`;

    const data: GenerateTanDialogData = {
      customerId: ua.customerId,
      userAccountId: ua.id,
      customerName,
      username: ua.username,
    };

    this.dialog.open(GenerateTanDialogComponent, { data });
  }

  /** Reveal state for the read-only Telephone Password row in Account Settings. */
  readonly telPwRevealed = signal(false);

  togglePhonePasswordReveal(): void {
    const ua = this.userAccount();
    if (!ua || !ua.phonePassword) return;
    const next = !this.telPwRevealed();
    this.telPwRevealed.set(next);
    if (next) this.logPhonePasswordReveal(ua);
  }

  private logPhonePasswordReveal(ua: UserAccountDetail): void {
    // EB-3798: never log the password value itself — only the audit metadata.
    this.loggingService.info('phone_password.revealed', {
      customerId: ua.customerId,
      userAccountId: ua.id,
      revealedBy: this.auth.currentUserId ?? null,
    });
  }
}
